import { useNavigate, useSearch } from '@tanstack/react-router';

/**
 * Glu entre `DataTable` (purement contrôlé, sans dépendance au routeur) et
 * l'URL — `02` §0 : « filtres conservés dans l'URL » et pagination 50 lignes.
 *
 * Générique (`useSearch({strict:false})`), comme `useOriginFilter` : n'importe
 * quel écran de liste peut l'utiliser sans que ce module connaisse sa route,
 * à condition que la route ajoute `page`/`sortBy`/`sortDir` (et ses propres
 * filtres) à son `validateSearch` (voir `README-shell.md`). Changer un filtre
 * remet toujours `page` à 1.
 */
export type TableSortDir = 'asc' | 'desc';

export interface DataTableUrlState {
  page: number;
  sortBy: string | undefined;
  sortDir: TableSortDir;
  setPage: (page: number) => void;
  setSort: (sortBy: string, sortDir: TableSortDir) => void;
  /** Fusionne des filtres arbitraires dans l'URL et remet `page` à 1. */
  setFilters: (
    patch: Record<string, string | number | boolean | undefined | readonly (string | number)[]>,
  ) => void;
}

function readSearchRecord(search: unknown): Record<string, unknown> {
  return typeof search === 'object' && search !== null ? (search as Record<string, unknown>) : {};
}

export function useDataTableUrlState(): DataTableUrlState {
  const search = readSearchRecord(useSearch({ strict: false }));
  const navigate = useNavigate();

  const rawPage = Number(search.page);
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const sortBy = typeof search.sortBy === 'string' ? search.sortBy : undefined;
  const sortDir: TableSortDir = search.sortDir === 'asc' ? 'asc' : 'desc';

  function setPage(next: number): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, page: next }),
      replace: true,
    });
  }

  function setSort(nextSortBy: string, nextSortDir: TableSortDir): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, sortBy: nextSortBy, sortDir: nextSortDir, page: 1 }),
      replace: true,
    });
  }

  function setFilters(
    patch: Record<string, string | number | boolean | undefined | readonly (string | number)[]>,
  ): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch, page: 1 }),
      replace: true,
    });
  }

  return { page, sortBy, sortDir, setPage, setSort, setFilters };
}
